import { useState } from 'react';

import { faChevronDown, faCircleNotch, faFileAlt, faTrash, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'lucide-react';

import { allowedFileTypes } from '../../../../../backend/src/case/doc/allowed-extensions';
import Button from '@/components/atoms/Button';
import { Checkbox } from '@/components/atoms/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/atoms/Tooltip';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/molecules/DropdownMenu';
import { ConfirmationModal } from '@/components/molecules/Modals/Confirmation';
import SearchBox from '@/components/molecules/SearchBox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/molecules/table';
import Pagination from '@/components/organisms/Pagination';
import formatDate from '@/helpers/formatDate';
import { cn } from '@/helpers/shadCnUtils';
import { File } from '@/types';

interface FileExplorerProps {
  isLoadingFiles: boolean;
  allFiles: File[];
  selectedFiles: Set<string>;
  setSelectedFiles: (files: Set<string>) => void;
  setFileType: (types: string) => void;
  fileType: string;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  handleFileSelect: (id: string) => void;
  handleBulkDelete: () => void;
  deleteAllFiles: () => void;
  isDeletingAllFiles: boolean;
  page: number;
  setPage: (page: number) => void;
  totalPages: number;
  isCaseCreator: boolean;
}

export const FileExplorer = ({
  isLoadingFiles,
  allFiles,
  selectedFiles,
  setSelectedFiles,
  setFileType,
  fileType,
  searchQuery,
  setSearchQuery,
  handleFileSelect,
  handleBulkDelete,
  deleteAllFiles,
  isDeletingAllFiles,
  page,
  setPage,
  totalPages,
  isCaseCreator = false,
}: FileExplorerProps) => {
  const [isDeleteAllFilesModalOpen, setIsDeleteAllFilesModalOpen] = useState(false);

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  return (
    <div className={cn('flex h-full min-h-0 w-full flex-col', isDeletingAllFiles ? 'cursor-progress' : '')}>
      <div
        className={cn(
          'mt-4 flex h-full w-full flex-col gap-4 overflow-hidden',
          isDeletingAllFiles ? 'pointer-events-none' : '',
        )}
      >
        <div className="flex flex-col gap-4 border-b border-gray-200 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {allFiles !== undefined && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={selectedFiles.size === allFiles.length && !isLoadingFiles}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedFiles(new Set(allFiles.map((f) => f.id)));
                      } else {
                        setSelectedFiles(new Set());
                      }
                    }}
                  />
                  <span className="min-w-18 text-sm font-semibold text-gray-800">
                    {selectedFiles.size > 0 ? `${selectedFiles.size} selected` : 'Select all'}
                  </span>
                </div>
              )}

              <div className="h-5 w-px bg-gray-200"></div>

              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleBulkDelete}
                      disabled={selectedFiles.size === 0}
                      className={`group flex h-8 items-center gap-2 rounded-md px-3 text-sm transition-all ${
                        selectedFiles.size === 0
                          ? 'cursor-not-allowed border border-white bg-gray-50 text-gray-400'
                          : 'border border-red-200 bg-white text-red-600 hover:bg-red-50 hover:shadow-sm'
                      }`}
                    >
                      <FontAwesomeIcon
                        icon={faTrash}
                        className={`h-3.5 w-3.5 transition-transform ${
                          selectedFiles.size > 0 ? 'group-hover:scale-110' : ''
                        }`}
                      />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {selectedFiles.size === 0
                      ? 'Select files to delete'
                      : `Delete ${selectedFiles.size} file${selectedFiles.size === 1 ? '' : 's'}`}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <div className="flex items-center text-sm">
                <DropdownMenu>
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <button
                            className={`flex h-8 items-center gap-2 rounded-md px-2 transition-all ${
                              fileType !== ''
                                ? 'rounded-r-none bg-blue-50 text-blue-600 hover:bg-blue-100 hover:shadow-sm'
                                : 'bg-gray-50 text-gray-600 hover:bg-gray-100 hover:shadow-sm'
                            }`}
                          >
                            <span className="text-sm font-medium">
                              {fileType.length > 0 ? `${fileType.toUpperCase().replace('.', '')}` : 'File type'}
                            </span>
                            <FontAwesomeIcon icon={faChevronDown} className="h-3.5 w-3.5" />
                          </button>
                        </DropdownMenuTrigger>
                      </TooltipTrigger>
                      <TooltipContent>Filter by file type</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <DropdownMenuContent
                    align="end"
                    className="max-h-48 w-16 overflow-y-auto rounded-md border border-gray-100 bg-white shadow-lg"
                  >
                    {allowedFileTypes.map((type) => (
                      <DropdownMenuCheckboxItem
                        key={type}
                        checked={fileType === type}
                        onCheckedChange={(checked) => {
                          setFileType(checked ? type : '');
                        }}
                        className="cursor-pointer hover:bg-gray-100"
                      >
                        {type.toUpperCase().replace('.', '')}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                {fileType !== '' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFileType('');
                    }}
                    className="ml-0.5 h-8 rounded-l-none rounded-r-md bg-blue-50 px-1.5 hover:bg-blue-100"
                  >
                    <FontAwesomeIcon icon={faTimes} className="h-3 w-3 text-blue-600" />
                  </button>
                )}
              </div>
            </div>

            <SearchBox
              value={searchQuery}
              onSearchCall={(query) => setSearchQuery(query)}
              placeholder="Search files..."
            />
          </div>
        </div>

        <div
          className={cn(
            'flex min-h-0 w-full flex-1 flex-shrink flex-col gap-2 overflow-hidden rounded-lg border bg-white',
            isCaseCreator ? 'max-h-96' : 'max-h-72',
          )}
        >
          {isLoadingFiles ? (
            <div className="flex h-full w-full items-center justify-center py-8">
              <FontAwesomeIcon icon={faCircleNotch} className="fa-spin text-gray-400" />
            </div>
          ) : (
            <>
              {allFiles && allFiles.length === 0 ? (
                <div className="flex h-full min-h-20 flex-col items-center justify-center gap-2 text-center">
                  <FontAwesomeIcon icon={faFileAlt} className="h-8 w-8 text-gray-400" />
                  <div className="text-sm text-gray-600">
                    {searchQuery || fileType !== '' ? 'No files match your filters' : 'No files uploaded yet'}
                  </div>
                </div>
              ) : (
                <Table>
                  <TableHeader className="sticky top-0">
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>Uploaded</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allFiles.map((file) => {
                      const extension = file.name.split('.').pop()?.toUpperCase() || '';

                      return (
                        <TableRow
                          key={file.id}
                          data-state={selectedFiles.has(file.id) ? 'selected' : undefined}
                          className={`cursor-pointer ${selectedFiles.has(file.id) ? 'bg-blue-50' : ''}`}
                          onClick={() => handleFileSelect(file.id)}
                        >
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Checkbox
                                checked={selectedFiles.has(file.id)}
                                onCheckedChange={() => handleFileSelect(file.id)}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <div className="flex items-center gap-2">
                                <FontAwesomeIcon icon={faFileAlt} className="h-4 w-4 text-gray-400" />
                                <span className="text-sm text-gray-900">{file.name}</span>
                                {file.parentFile && (
                                  <TooltipProvider delayDuration={0}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Link className="h-3.5 w-3.5 text-gray-400 transition-colors hover:text-gray-600" />
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Attachment of {file.parentFile.name}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                              {extension}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-gray-600">{formatFileSize(file.size)}</span>
                          </TableCell>
                          <TableCell>
                            <span className="text-xs text-gray-600">{formatDate(file.createdDate, true, true)}</span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </div>

        {isCaseCreator ? (
          <div className="flex justify-between">
            <Button
              type="delete"
              disabled={allFiles.length === 0 || isDeletingAllFiles}
              onClick={() => setIsDeleteAllFilesModalOpen(true)}
              text="Delete All Files"
              rounded="base"
              size="small"
              loading={isDeletingAllFiles}
            />
            <Pagination
              canGetNextPage={page < totalPages}
              canGetPrevPage={page > 1}
              prevPage={() => setPage(page - 1)}
              nextPage={() => setPage(page + 1)}
              currentPage={page - 1}
              noOfPages={totalPages}
              goToPage={(page) => setPage(page + 1)}
            />
          </div>
        ) : (
          <div className="flex justify-end">
            <Pagination
              canGetNextPage={page < totalPages}
              canGetPrevPage={page > 1}
              prevPage={() => setPage(page - 1)}
              nextPage={() => setPage(page + 1)}
              currentPage={page - 1}
              noOfPages={totalPages}
              goToPage={(page) => setPage(page + 1)}
            />
          </div>
        )}
      </div>
      <ConfirmationModal
        isOpen={isDeleteAllFilesModalOpen}
        handleClose={() => setIsDeleteAllFilesModalOpen(false)}
        title="Delete All Files"
        description="Are you sure? This will delete all of the uploaded files."
        onConfirm={() => {
          deleteAllFiles();
          setIsDeleteAllFilesModalOpen(false);
        }}
        isLoading={isDeletingAllFiles}
        confirmButtonText="Delete Files"
      />
    </div>
  );
};
